// src/pages/Order/Order.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft, CalendarDays, CheckCircle2, ChevronLeft, Copy, CreditCard, FileText, Headphones, MapPin, Package, ShieldCheck, ShoppingBag, Truck, UserRound, ExternalLink, Phone, X, Loader2,
} from "lucide-react";
import { getOrderDetails } from "../../redux/features/Order/orderThunk";
import OrderServices from "../../services/orderServices";
import Loader from "../../components/Loader";
import Message from "../../components/Message";

const formatToman = (value) => {
  const num = Number(value);
  if (isNaN(num)) return '۰ تومان';
  return Math.round(num).toLocaleString('fa-IR') + ' تومان';
};

const Toast = ({ message, isVisible, onClose }) => {
  if (!isVisible) return null;
  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 fade-in duration-300">
      <div className="flex items-center gap-3 rounded-2xl bg-emerald-50 px-5 py-3.5 shadow-xl shadow-emerald-200/50 border border-emerald-200 backdrop-blur-sm">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
          <CheckCircle2 className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-bold text-emerald-800">{message}</p>
          <p className="text-[11px] text-emerald-600">کد با موفقیت کپی شد</p>
        </div>
        <button onClick={onClose} className="mr-2 rounded-lg p-1 text-emerald-400 transition hover:bg-emerald-100 hover:text-emerald-600">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const CallModal = ({ isOpen, onClose, onCall }) => {
  if (!isOpen) return null;
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md animate-in zoom-in-95 duration-200">
          <div className="relative overflow-hidden rounded-3xl bg-white shadow-2xl shadow-slate-900/30">
            <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-[#9EAD8C]/20 blur-2xl" />
            <div className="relative p-6">
              <div className="text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#9EAD8C]/10 text-[#8A9A7B]">
                  <Headphones className="h-8 w-8" />
                </div>
                <h3 className="mt-4 text-xl font-black text-slate-900">تماس با پشتیبانی</h3>
                <p className="mt-2 text-sm text-slate-500">آیا مایل به تماس با پشتیبانی هستید؟</p>
                <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-center">
                  <p className="text-xs text-slate-400">شماره تماس پشتیبانی</p>
                  <p dir="ltr" className="mt-1 text-lg font-black text-slate-900">09903607525</p>
                </div>
                <div className="mt-6 flex gap-3">
                  <button onClick={onClose} className="flex-1 rounded-2xl border-2 border-slate-200 px-4 py-3 text-sm font-bold text-slate-600 transition hover:bg-slate-50">انصراف</button>
                  <button onClick={onCall} className="flex-1 rounded-2xl bg-[#9EAD8C] px-4 py-3 text-sm font-bold text-white shadow-lg shadow-[#9EAD8C]/30 transition hover:bg-[#8A9A7B] active:scale-95">
                    <Phone className="ml-2 inline h-4 w-4" />
                    زنگ بزن
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const SectionCard = ({ icon: Icon, title, eyebrow, children, className = "" }) => {
  return (
    <section className={`relative overflow-hidden rounded-3xl border border-[#9EAD8C]/30 bg-white shadow-[0_14px_40px_-28px_rgba(158,173,140,.4)] ${className}`}>
      <div className="pointer-events-none absolute -left-10 -top-12 h-28 w-28 rounded-full bg-[#9EAD8C]/20 blur-2xl" />
      <div className="relative flex items-center gap-3 border-b border-[#9EAD8C]/20 px-5 py-4 sm:px-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#9EAD8C] text-white shadow-lg shadow-[#9EAD8C]/30">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
        <div>
          {eyebrow && <p className="mb-0.5 text-[10px] font-bold tracking-widest text-[#8A9A7B]">{eyebrow}</p>}
          <h2 className="text-base font-extrabold text-slate-900">{title}</h2>
        </div>
      </div>
      <div className="relative p-4 sm:p-6">{children}</div>
    </section>
  );
};

const InfoItem = ({ icon: Icon, label, value, valueClassName = "" }) => {
  return (
    <div className="group flex min-w-0 items-start gap-3 rounded-2xl border border-[#9EAD8C]/20 bg-slate-50/70 p-3.5 transition hover:border-[#9EAD8C] hover:bg-white hover:shadow-sm">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[#8A9A7B] shadow-sm ring-1 ring-[#9EAD8C]/30 group-hover:text-[#9EAD8C]">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium text-[#8A9A7B]">{label}</p>
        <p className={`mt-1 break-words text-xs font-bold leading-6 text-slate-800 ${valueClassName}`}>{value}</p>
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value, strong = false }) => {
  return (
    <div className={`flex items-center justify-between gap-4 py-2.5 ${strong ? "mt-2 border-t border-dashed border-[#9EAD8C]/30 pt-4" : ""}`}>
      <span className={strong ? "text-sm font-extrabold text-slate-900" : "text-xs text-slate-500"}>{label}</span>
      <span dir="ltr" className={strong ? "text-base font-black text-[#8A9A7B]" : "text-sm font-bold text-slate-700"}>{value}</span>
    </div>
  );
};

const StatusBadge = ({ children }) => {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#9EAD8C]/20 px-3 py-1.5 text-[11px] font-extrabold text-[#8A9A7B] ring-1 ring-inset ring-[#9EAD8C]/30">
      <CheckCircle2 className="h-3.5 w-3.5 text-[#8A9A7B]" aria-hidden="true" />
      {children}
    </span>
  );
};

const ProductThumbnail = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);
  return (
    <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-[#9EAD8C]/30 bg-slate-100 shadow-inner sm:h-28 sm:w-28">
      {hasError ? (
        <Package className="h-10 w-10 text-[#9EAD8C]" aria-label="تصویر محصول موجود نیست" />
      ) : (
        <img src={src} alt={alt} onError={() => setHasError(true)} className="h-full w-full object-cover transition duration-500 hover:scale-105" />
      )}
    </div>
  );
};

const ActionButton = ({ icon: Icon, children, primary = false, onClick, title, disabled = false }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title || children}
      disabled={disabled}
      className={`inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl px-3 text-xs font-extrabold transition focus:outline-none focus:ring-4 focus:ring-[#9EAD8C]/50 active:scale-[.98] disabled:opacity-50 disabled:cursor-not-allowed ${
        primary
          ? "bg-[#9EAD8C] text-white shadow-lg shadow-[#9EAD8C]/30 hover:bg-[#8A9A7B]"
          : "border border-[#9EAD8C] bg-white text-[#8A9A7B] hover:bg-[#9EAD8C]/10 hover:border-[#8A9A7B]"
      }`}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {children}
    </button>
  );
};

const Order = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userInfo } = useSelector((state) => state.auth) || {};
  const { loading, order, error } = useSelector((state) => state.orderDetails) || {};

  const [showToast, setShowToast] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [payGateMsg, setPayGateMsg] = useState("");
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!userInfo?.access) {
      navigate(`/phone?redirect=/order/${orderId}`);
      return;
    }
    if (!orderId) {
      navigate("/profile");
      return;
    }
    const currentOrderId = order?._id?.toString();
    if (orderId !== currentOrderId) {
      dispatch(getOrderDetails(orderId));
    }
  }, [orderId, dispatch, userInfo, navigate, order?._id]);

  const itemsPrice = order?.orderItems?.reduce((acc, item) => acc + Number(item.price) * Number(item.qty), 0) || 0;

  const goPayGate = () => {
    setPayGateMsg("");
    setIsConnected(false);
    if (!order || !order._id) {
      setPayGateMsg("اطلاعات سفارش کامل نیست.");
      return;
    }
    OrderServices.payOrder(order._id, { userInfo, dispatch })
      .then((response) => {
        setIsConnected(true);
        setTimeout(() => {
          window.location.assign(response.data.paymentLink);
        }, 5000);
      })
      .catch((error) => {
        setIsConnected(false);
        const msg = error.response?.data?.detail || error.message || "خطا در اتصال به درگاه پرداخت";
        setPayGateMsg(msg);
      });
  };

  const copyTrackingCode = async (trackingCode) => {
    if (!trackingCode || trackingCode === "---") return;
    try {
      await navigator.clipboard.writeText(trackingCode);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = trackingCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  };

  const handleOpenCallModal = () => setShowCallModal(true);
  const handleCloseCallModal = () => setShowCallModal(false);
  const handleMakeCall = () => {
    window.location.href = "tel:09903607525";
    setShowCallModal(false);
  };

  // ✅ Early returns قبل از orderData (hooks بالا بودن رعایت شده)
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="w-full max-w-md">
          <Message variant="danger" text={error} />
          <button
            onClick={() => dispatch(getOrderDetails(orderId))}
            className="mt-4 w-full rounded-xl bg-[#9EAD8C] px-4 py-2 text-sm font-bold text-white hover:bg-[#8A9A7B] transition"
          >
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  if (!order || !order._id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="w-full max-w-md">
          <Message variant="info" text="سفارش یافت نشد" />
          <button
            onClick={() => navigate("/profile")}
            className="mt-4 w-full rounded-xl bg-[#9EAD8C] px-4 py-2 text-sm font-bold text-white hover:bg-[#8A9A7B] transition"
          >
            بازگشت به پروفایل
          </button>
        </div>
      </div>
    );
  }

  // ✅ orderData اول، بعد از early returns
  const orderData = {
    id: order._id,
    status: order.isDelivered ? "تحویل شده" : order.isPaid ? "در حال ارسال" : "در انتظار پرداخت",
    date: order.createdAt ? new Date(order.createdAt).toLocaleDateString("fa-IR") : "---",
    time: order.createdAt
      ? new Date(order.createdAt).toLocaleTimeString("fa-IR", { hour: "2-digit", minute: "2-digit" })
      : "---",
    customer: {
      name: order.user?.name || "کاربر",
      phone: order.user?.phone || "---",
      address: order.shippingAddress
        ? `${order.shippingAddress.country || ""}، ${order.shippingAddress.city || ""}، ${order.shippingAddress.address || ""}${
            order.shippingAddress.postalCode ? ` / کد پستی: ${order.shippingAddress.postalCode}` : ""
          }`
        : "آدرسی ثبت نشده",
      shippingDate: order.deliveredAt
        ? new Date(order.deliveredAt).toLocaleDateString("fa-IR") +
          " ساعت " +
          new Date(order.deliveredAt).toLocaleTimeString("fa-IR", { hour: "2-digit", minute: "2-digit" })
        : "---",
      shippingStatus: order.isDelivered ? "تحویل شده" : order.isPaid ? "در حال ارسال" : "در انتظار پرداخت",
    },
    payment: {
      method: order.paymentMethod || "زیبال",
      date: order.paidAt
        ? new Date(order.paidAt).toLocaleDateString("fa-IR") +
          " ساعت " +
          new Date(order.paidAt).toLocaleTimeString("fa-IR", { hour: "2-digit", minute: "2-digit" })
        : "---",
      status: order.isPaid ? "موفق" : "در انتظار",
    },
    product:
      order.orderItems && order.orderItems.length > 0
        ? {
            name: order.orderItems[0].name || "محصول",
            color: order.orderItems[0].selected_color_name || "---",
            price: Number(order.orderItems[0].price) || 0,
            quantity: Number(order.orderItems[0].qty) || 1,
            total: Number(order.orderItems[0].price) * Number(order.orderItems[0].qty) || 0,
            image: order.orderItems[0].image || "https://via.placeholder.com/400",
          }
        : null,
    summary: {
      subtotal: itemsPrice,
      shipping: Number(order.shippingPrice) || 0,
      tax: Number(order.taxPrice) || 0,
      total: Number(order.totalPrice) || 0,
    },
    trackingCode: order.tracking_code || "---",
    shippingMethod: order.shipping_method || "post",
    supportPhone: "09903607525",
    isPaid: order.isPaid,
  };

  const hasProduct = orderData.product !== null;
  const hasTracking = orderData.trackingCode && orderData.trackingCode !== "---";
  const isTipax = orderData.shippingMethod === "tipax";
  const trackingLabel = isTipax ? "کد رهگیری تیپاکس" : "کد رهگیری پست";

  // ✅ handleTrackOrder بعد از orderData تعریف می‌شود
  const handleTrackOrder = () => {
    if (!hasTracking) {
      alert("کد رهگیری برای این سفارش ثبت نشده است.");
      return;
    }
    // کپی خودکار کد برای paste سریع در سایت مقصد
    navigator.clipboard?.writeText(orderData.trackingCode).catch(() => {});
    if (isTipax) {
      window.open("https://tipaxco.com/tracking", "_blank", "noopener,noreferrer");
    } else {
      window.open("https://tracking.post.ir/", "_blank", "noopener,noreferrer");
    }
  };

  return (
    <>
      <Toast message="کد رهگیری پستی شما با موفقیت کپی شد" isVisible={showToast} onClose={() => setShowToast(false)} />
      <CallModal isOpen={showCallModal} onClose={handleCloseCallModal} onCall={handleMakeCall} />

      <main dir="rtl" className="min-h-screen bg-slate-100 px-4 py-5 text-slate-900 sm:px-6 lg:px-8 lg:py-8">
        <div className="mx-auto max-w-7xl">
          <header className="relative mb-6 overflow-hidden rounded-3xl bg-[#9EAD8C] px-5 py-6 text-white shadow-2xl shadow-[#9EAD8C]/30 sm:px-8">
            <div className="pointer-events-none absolute -left-16 -top-20 h-64 w-64 rounded-full bg-white/20 blur-3xl" />
            <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="mb-3 inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold text-white transition hover:bg-white/20"
                >
                  <ArrowLeft className="h-4 w-4" />
                  بازگشت
                </button>
                <div className="mb-2 flex flex-wrap items-center gap-3">
                  <span className="text-xs text-white/70">
                    داشبورد <ChevronLeft className="mx-1 inline h-3 w-3" /> سفارش‌ها
                  </span>
                  <StatusBadge>{orderData.status}</StatusBadge>
                </div>
                <h1 className="text-2xl font-black tracking-tight sm:text-3xl">
                  جزئیات سفارش <span dir="ltr" className="text-white/80">#{orderData.id}</span>
                </h1>
              </div>
              <div className="rounded-2xl border border-white/15 bg-white/10 p-2 backdrop-blur-sm sm:min-w-52">
                <div className="mb-2 flex items-center gap-2 text-xs text-white/70">
                  <FileText className="h-4 w-4" />
                  زمان ثبت سفارش
                </div>
                <p className="text-sm font-bold">{orderData.date}</p>
                <p dir="ltr" className="text-xs text-white/60">{orderData.time}</p>
              </div>
            </div>
          </header>

          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="space-y-6">
              <SectionCard icon={Truck} eyebrow="DELIVERY INFORMATION" title="اطلاعات ارسال">
                <div className="grid gap-3 sm:grid-cols-2">
                  <InfoItem icon={UserRound} label="نام مشتری" value={orderData.customer.name} />
                  <InfoItem icon={Phone} label="شماره تلفن" value={orderData.customer.phone} valueClassName="dir-ltr" />
                  <div className="sm:col-span-2">
                    <InfoItem icon={MapPin} label="آدرس تحویل" value={orderData.customer.address} />
                  </div>
                  <InfoItem icon={CalendarDays} label="تاریخ ارسال" value={orderData.customer.shippingDate} />
                  <InfoItem
                    icon={CheckCircle2}
                    label="وضعیت ارسال"
                    value={orderData.customer.shippingStatus}
                    valueClassName="text-emerald-600"
                  />
                </div>
              </SectionCard>

              <SectionCard icon={CreditCard} eyebrow="SECURE PAYMENT" title="روش پرداخت">
                <div className="grid gap-3 sm:grid-cols-2">
                  <InfoItem icon={CreditCard} label="روش پرداخت" value={orderData.payment.method} />
                  <InfoItem icon={CalendarDays} label="تاریخ پرداخت" value={orderData.payment.date} />
                </div>
                <div className="mt-3 flex items-center justify-between rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                  <div>
                    <p className="text-[11px] font-medium text-emerald-700/70">وضعیت تراکنش</p>
                    <StatusBadge>{orderData.payment.status}</StatusBadge>
                  </div>
                  <ShieldCheck className="h-8 w-8 text-emerald-500" />
                </div>
              </SectionCard>

              {hasProduct && (
                <SectionCard icon={ShoppingBag} eyebrow="ORDER ITEMS" title="محصول سفارش داده‌شده">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <ProductThumbnail src={orderData.product.image} alt={orderData.product.name} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="text-base font-black">{orderData.product.name}</h3>
                          <p className="mt-1 text-xs text-slate-500">
                            رنگ: <b className="text-slate-700">{orderData.product.color}</b>
                          </p>
                        </div>
                        <span className="rounded-xl bg-[#9EAD8C]/10 px-3 py-1.5 text-xs font-bold text-[#8A9A7B]">
                          تعداد: {orderData.product.quantity}
                        </span>
                      </div>
                      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#9EAD8C]/20 pt-4">
                        <span dir="ltr" className="text-sm font-bold text-slate-500">
                          {formatToman(orderData.product.price)} × {orderData.product.quantity}
                        </span>
                        <span dir="ltr" className="text-lg font-black text-[#8A9A7B]">
                          {formatToman(orderData.product.total)}
                        </span>
                      </div>
                    </div>
                  </div>
                </SectionCard>
              )}
            </div>

            <aside className="space-y-6 lg:sticky lg:top-6">
              <SectionCard icon={FileText} eyebrow="PAYMENT SUMMARY" title="خلاصه سفارش">
                <SummaryRow label="مبلغ کالا" value={formatToman(orderData.summary.subtotal)} />
                <SummaryRow label="پس کرایه" value={formatToman(orderData.summary.shipping)} />
                <SummaryRow label="مجموع نهایی" value={formatToman(orderData.summary.total)} strong />

                {!orderData.isPaid && (
                  <div className="mt-4">
                    {isConnected ? (
                      <div className="rounded-xl bg-emerald-50 p-3 text-center text-sm text-emerald-700 border border-emerald-200">
                        در حال انتقال به درگاه پرداخت...
                      </div>
                    ) : payGateMsg ? (
                      <div className="rounded-xl bg-red-50 p-3 text-center text-sm text-red-600 border border-red-200">
                        {payGateMsg}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={goPayGate}
                      disabled={isConnected}
                      className="mt-3 w-full rounded-2xl bg-[#9EAD8C] px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#9EAD8C]/30 transition hover:bg-[#8A9A7B] active:scale-[.98] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      پرداخت سفارش
                    </button>
                  </div>
                )}

                {/* ✅ کد رهگیری — لیبل داینامیک پست/تیپاکس */}
                <div className="mt-5 flex items-center gap-3 rounded-2xl bg-[#9EAD8C]/10 p-4 border border-[#9EAD8C]/30">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#9EAD8C] text-white shadow-sm">
                    <Truck className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[11px] font-medium text-[#8A9A7B]">{trackingLabel}</p>
                    <p dir="ltr" className="mt-1 text-sm font-black text-slate-900">
                      {hasTracking ? orderData.trackingCode : "در انتظار ثبت"}
                    </p>
                  </div>
                  {hasTracking && (
                    <button
                      type="button"
                      onClick={() => copyTrackingCode(orderData.trackingCode)}
                      className="rounded-lg p-2 text-[#8A9A7B] hover:bg-white hover:text-[#9EAD8C] transition"
                      aria-label="کپی کد رهگیری"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="mt-5 flex gap-3">
                  <ActionButton
                    icon={ExternalLink}
                    primary
                    title="پیگیری سفارش"
                    onClick={handleTrackOrder}
                    disabled={!hasTracking}
                  >
                    پیگیری سفارش
                  </ActionButton>
                  <ActionButton icon={Headphones} title="تماس با پشتیبانی" onClick={handleOpenCallModal}>
                    پشتیبانی
                  </ActionButton>
                </div>
              </SectionCard>

              <div className="rounded-3xl border border-[#9EAD8C]/30 bg-gradient-to-br from-[#9EAD8C]/10 to-white p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#9EAD8C]/20 text-[#8A9A7B]">
                    <Headphones className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold text-slate-900">به کمک نیاز دارید؟</h3>
                    <p className="mt-1 text-xs text-slate-500">تیم پشتیبانی در کنار شماست.</p>
                    <button
                      onClick={handleOpenCallModal}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[#8A9A7B] hover:text-[#9EAD8C]"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {orderData.supportPhone}
                    </button>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
};

export default Order;